import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import './Login.css';

const Login = ({ onLoginSuccess, redirectMessage }) => {
  // --- Global Auth Context ---
  // Destructured requestOtp and verifyOtp from useAuth
  const { 
    login, 
    error: authError, 
    clearError, 
    isLoading: isAuthLoading, 
    loginWithGoogle,
    requestOtp,
    verifyOtp
  } = useAuth();

  // --- Local UI State ---
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [otpStep, setOtpStep] = useState(1); // Step 1: Request, Step 2: Verify
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // --- Form Data State ---
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    identifier: '', 
    otp: ''
  });

  const isLoading = isAuthLoading || localLoading;
  const displayError = localError || authError;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setLocalError('');
    clearError();
    setSuccessMessage('');
  };

  const switchLoginMethod = (method) => {
    setLoginMethod(method);
    setOtpStep(1);
    setLocalError('');
    clearError();
    setSuccessMessage('');
  };

  // --- PASSWORD LOGIN LOGIC ---
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setLocalError('Please enter both email and password.');
      return;
    }

    const result = await login({ email: formData.email, password: formData.password });
    if (result.success) onLoginSuccess(result.user, result);
  };

  // --- OTP LOGIN LOGIC ---
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!formData.identifier) {
      setLocalError('Please enter your email or phone number.');
      return;
    }

    setLocalLoading(true);
    setLocalError('');
    try {
      // Call requestOtp from AuthContext instead of apiService directly
      const response = await requestOtp(formData.identifier);
      if (response.success) {
        setSuccessMessage('OTP sent! Please check your Email and WhatsApp.');
        setOtpStep(2);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to send OTP.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    // 1. Clean the OTP by removing any accidental leading/trailing spaces
    const cleanOtp = formData.otp.trim();

    // 2. Use the cleaned OTP for length validation
    if (!cleanOtp || cleanOtp.length !== 6) {
      setLocalError('Please enter a valid 6-digit OTP.');
      return;
    }

    setLocalLoading(true);
    setLocalError('');
    try {
      // 3. Send the cleaned OTP to the backend via AuthContext verifyOtp
      const response = await verifyOtp(formData.identifier, cleanOtp);
      
      if (response.success) {
        onLoginSuccess(response.user, response);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setLocalError(err.message || 'Invalid OTP.');
    } finally {
      setLocalLoading(false);
    }
  };

  // --- GOOGLE LOGIN LOGIC ---
  const handleGoogleSuccess = async (credentialResponse) => {
    const result = await loginWithGoogle(credentialResponse.credential);
    if (result.success) onLoginSuccess(result.user, result);
  };
  const handleGoogleError = () => { setLocalError('Google Sign-In failed.'); };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Welcome Back</h2>
          <p>Sign in to continue your spiritual journey</p>
        </div>

        {/* --- GOOGLE LOGIN --- */}
        <div className="google-login-container">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="filled_blue" 
            shape="rectangular" 
            logo_alignment="center" 
            width="100%"
          />
        </div>
        
        <div className="separator">OR SIGN IN WITH</div>

        {/* Messages */}
        {redirectMessage && <div className="info-message" role="alert">{redirectMessage}</div>}
        {successMessage && <div className="info-message success" role="status">{successMessage}</div>}
        {displayError && <div className="error-message general-error" role="alert">{displayError}</div>}

        {/* --- TAB NAVIGATION --- */}
        <div className="auth-tabs" role="tablist">
          <button
            className={`auth-tab ${loginMethod === 'password' ? 'active' : ''}`}
            onClick={() => switchLoginMethod('password')}
            type="button"
            role="tab"
            aria-selected={loginMethod === 'password'}
          >
            Password
          </button>
          <button
            className={`auth-tab ${loginMethod === 'otp' ? 'active' : ''}`}
            onClick={() => switchLoginMethod('otp')}
            type="button"
            role="tab"
            aria-selected={loginMethod === 'otp'}
          >
            One-Time Password (OTP)
          </button>
        </div>

        {/* --- TAB PANELS --- */}
        <div className="tab-content">
          {loginMethod === 'password' ? (
            <form onSubmit={handlePasswordSubmit} className="auth-form" role="tabpanel">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  placeholder="Enter your email" 
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input 
                  type="password" 
                  id="password" 
                  name="password" 
                  value={formData.password} 
                  onChange={handleChange} 
                  placeholder="Enter your password" 
                  required
                />
              </div>
              <div className="form-options">
                <label className="checkbox-container">
                  <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleChange} />
                  <span className="checkmark"></span> Remember me
                </label>
                <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
              </div>
              <button type="submit" className="auth-button" disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <div className="auth-form" role="tabpanel">
              {otpStep === 1 ? (
                <form onSubmit={handleRequestOtp}>
                  <div className="form-group">
                    <label htmlFor="identifier">Email or Phone Number</label>
                    <input 
                      type="text" 
                      id="identifier" 
                      name="identifier" 
                      value={formData.identifier} 
                      onChange={handleChange} 
                      placeholder="e.g. user@email.com or 9876543210" 
                      required 
                    />
                  </div>
                  <button type="submit" className="auth-button" disabled={isLoading}>
                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp}>
                  <div className="form-group">
                    <label htmlFor="otp">Enter 6-Digit OTP</label>
                    <input 
                      type="text" 
                      id="otp" 
                      name="otp" 
                      value={formData.otp} 
                      onChange={handleChange} 
                      placeholder="• • • • • •" 
                      maxLength="6" 
                      className="otp-input"
                      required 
                    />
                  </div>
                  <button type="submit" className="auth-button" disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                  </button>
                  <div className="resend-container">
                    <span className="resend-text">Didn't receive the code?</span>
                    <button type="button" className="text-btn" onClick={handleRequestOtp} disabled={isLoading}>
                      Resend OTP
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        <div className="auth-footer">
          <p className="google-terms-notice">
            By signing in, you agree to our <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
          </p>
          <p className="signup-prompt">
            Don't have an account? <Link to="/signup" className="auth-link">Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
